import {
  ActivityLogManyQuery,
  EnumActivityLogType,
  EnumEntityReferenceType,
} from "gql/generated";
import { getMultilingualString } from "./getMultilingualString";
import { TFunction } from "i18next";

export type ActivityLogFilter =
  | "ALL"
  | "OPERATOR"
  | "EVENTS"
  | "PERSON"
  | "BROADCAST"
  | "COURSE"
  | "LOGIN"
  | "EMAIL";

export const filterLogTypeMap: Record<EnumActivityLogType, ActivityLogFilter> = {
  [EnumActivityLogType.OperatorCreated]: "OPERATOR",
  [EnumActivityLogType.OperatorUpdated]: "OPERATOR",
  [EnumActivityLogType.OperatorStaffAdded]: "OPERATOR",
  [EnumActivityLogType.EventSubmitted]: "EVENTS",
  [EnumActivityLogType.InquiryReceived]: "PERSON",
  [EnumActivityLogType.BroadcastPublished]: "BROADCAST",
  [EnumActivityLogType.BroadcastViewed]: "BROADCAST",
  [EnumActivityLogType.ResourceViewed]: "BROADCAST",
  [EnumActivityLogType.FormCompleted]: "PERSON",
  [EnumActivityLogType.FormDraftSaved]: "PERSON",
  [EnumActivityLogType.FormResubmitted]: "PERSON",
  [EnumActivityLogType.FormSubmissionApproved]: "PERSON",
  [EnumActivityLogType.FormSubmissionRejected]: "PERSON",
  [EnumActivityLogType.VideoPlayed]: "BROADCAST",
  [EnumActivityLogType.PhotosDownloaded]: "BROADCAST",
  [EnumActivityLogType.FileDownloaded]: "BROADCAST",
  [EnumActivityLogType.CourseStarted]: "COURSE",
  [EnumActivityLogType.CourseCompleted]: "COURSE",
  [EnumActivityLogType.UserLoggedIn]: "LOGIN",
  [EnumActivityLogType.DirectoryUpdated]: "EVENTS",
  [EnumActivityLogType.EmailDailyDigestSent]: "EMAIL",
  [EnumActivityLogType.EmailInviteSent]: "EMAIL",
  [EnumActivityLogType.EmailBroadcastNotificationSent]: "EMAIL",
  [EnumActivityLogType.EmailRenewalSent]: "EMAIL",
  [EnumActivityLogType.EmailPaymentRequestSent]: "EMAIL",
} as Record<EnumActivityLogType, ActivityLogFilter>;

type ActivityLog = ActivityLogManyQuery["activityLogMany"][0];

/**
 * Connect-via-Adminland used to fan a global publication-status diff out to
 * every org that shares the listing (attributed to Whereabot AI). Hide those
 * from org activity feeds; "Added to directory" and real listing edits remain.
 */
export function isConnectStatusChangeNoise(log: {
  type?: string | null;
  initiatedBy?: string | null;
  message?: ActivityLog["message"];
}): boolean {
  if (log.type !== EnumActivityLogType.OperatorUpdated) return false;
  if (log.initiatedBy !== "Whereabot AI") return false;
  if (!log.message) return false;
  return getMultilingualString(log.message)
    .toLowerCase()
    .includes("status changed from");
}

export function getFilterForLog(log: ActivityLog): ActivityLogFilter | undefined {
  if (log.type === EnumActivityLogType.DirectoryUpdated) {
    return log.entity?.type === EnumEntityReferenceType.Event
      ? "EVENTS"
      : undefined;
  }
  const t = String(log.type ?? "");
  return (
    filterLogTypeMap[t as EnumActivityLogType] ??
    (t === "EMAIL_INVITE_SENT" ||
    t === "EMAIL_DAILY_DIGEST_SENT" ||
    t === "EMAIL_BROADCAST_NOTIFICATION_SENT" ||
    t === "EMAIL_RENEWAL_SENT" ||
    t === "EMAIL_PAYMENT_REQUEST_SENT"
      ? "EMAIL"
      : undefined)
  );
}

/**
 * Inverts filterLogTypeMap to get all EnumActivityLogType values for a set of
 * UI filters. Useful for server-side `types` filtering.
 */
export function getTypesForFilters(
  filters: ActivityLogFilter[],
): EnumActivityLogType[] {
  if (filters.includes("ALL") || filters.length === 0) return [];
  const types: EnumActivityLogType[] = [];
  for (const [logType, filter] of Object.entries(filterLogTypeMap)) {
    if (filters.includes(filter)) {
      types.push(logType as EnumActivityLogType);
    }
  }
  return types;
}

export type ActivityFeedButtonVisual =
  | "primary"
  | "warning"
  | "error"
  | "success"
  | "highlight"
  | "accent.magenta"
  | "accent.blue"
  | "accent.purple";

export type ActivityLogCardData = {
  title: string;
  boldedText: string;
  cta: string;
  visual: ActivityFeedButtonVisual;
  titleFormat: "default" | "eventSubmitted" | "operatorAdded";
  hideAction: boolean;
};

function formatPaymentRequestEmailTitle(messageStr: string): string {
  const invoiceMatch = messageStr.match(/for invoice ([\w.-]+)/i);
  if (invoiceMatch?.[1]) {
    if (/^invoice email resent\b/i.test(messageStr)) {
      return `Invoice email resent for ${invoiceMatch[1]}`;
    }
    if (/^updated invoice email sent\b/i.test(messageStr)) {
      return `Updated invoice email sent for ${invoiceMatch[1]}`;
    }
    return `Invoice email sent for ${invoiceMatch[1]}`;
  }

  // Legacy logs stored the Mongo payment-request id — hide it in the feed.
  if (/payment request [a-f0-9]{24}/i.test(messageStr)) {
    if (/^invoice email resent\b/i.test(messageStr)) return "Invoice email resent";
    return "Invoice email sent";
  }

  return messageStr.trim() || "Invoice email sent";
}

function isPaymentRequestEmailType(
  type: EnumActivityLogType | string,
  typeStr: string,
): boolean {
  return (
    type === EnumActivityLogType.EmailPaymentRequestSent ||
    typeStr === "EMAIL_PAYMENT_REQUEST_SENT"
  );
}

function isEmailType(type: EnumActivityLogType | string): boolean {
  const t = String(type ?? "");
  return (
    type === EnumActivityLogType.EmailDailyDigestSent ||
    type === EnumActivityLogType.EmailInviteSent ||
    type === EnumActivityLogType.EmailBroadcastNotificationSent ||
    type === EnumActivityLogType.EmailRenewalSent ||
    type === EnumActivityLogType.EmailPaymentRequestSent ||
    t === "EMAIL_DAILY_DIGEST_SENT" ||
    t === "EMAIL_INVITE_SENT" ||
    t === "EMAIL_BROADCAST_NOTIFICATION_SENT" ||
    t === "EMAIL_RENEWAL_SENT" ||
    t === "EMAIL_PAYMENT_REQUEST_SENT"
  );
}

function getVisual(type: EnumActivityLogType | string): ActivityFeedButtonVisual {
  if (
    type === EnumActivityLogType.OperatorCreated ||
    type === EnumActivityLogType.OperatorUpdated ||
    type === EnumActivityLogType.OperatorStaffAdded ||
    type === EnumActivityLogType.DirectoryUpdated
  )
    return "accent.blue";
  if (
    type === EnumActivityLogType.EventSubmitted ||
    type === EnumActivityLogType.CourseCompleted ||
    type === EnumActivityLogType.OrderPurchased
  )
    return "success";
  if (
    type === EnumActivityLogType.OrderPaymentPending ||
    type === EnumActivityLogType.OrderBookingRequest
  )
    return "highlight";
  if (
    type === EnumActivityLogType.InquiryReceived ||
    type === EnumActivityLogType.CourseStarted ||
    type === EnumActivityLogType.UserLoggedIn
  )
    return "accent.magenta";
  if (
    type === EnumActivityLogType.BroadcastPublished ||
    type === EnumActivityLogType.BroadcastViewed ||
    type === EnumActivityLogType.FormCompleted ||
    type === EnumActivityLogType.FormDraftSaved ||
    type === EnumActivityLogType.FormResubmitted ||
    type === EnumActivityLogType.FormSubmissionApproved ||
    type === EnumActivityLogType.FormSubmissionRejected ||
    type === EnumActivityLogType.ResourceViewed ||
    type === EnumActivityLogType.FileDownloaded ||
    type === EnumActivityLogType.PhotosDownloaded ||
    type === EnumActivityLogType.VideoPlayed
  )
    return "warning";
  if (isEmailType(type)) return "accent.blue";
  return "primary";
}

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, txt =>
    txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase(),
  );
}

function splitOnEntityName(message: string, entityName: string) {
  return message.split(entityName)[0] ?? message;
}

/**
 * Canonical function that maps an ActivityLog entry to display data.
 * Merged from both the org ActivityFeedCard and adminland ActivityLogDirectoryCard.
 */
export function getActivityLogCardData(
  activityLog: ActivityLog,
  t: TFunction,
): ActivityLogCardData {
  const { type, initiatedBy, message, entityName, importRunId } = activityLog;
  const displayName = importRunId ? "Whereabouts" : initiatedBy;
  const messageStr = getMultilingualString(message);
  const entityNameStr = getMultilingualString(entityName);
  const typeStr = String(type ?? "");

  const messageText = messageStr.toLowerCase();
  const hideAction =
    !!importRunId &&
    messageText.includes("created via import") &&
    messageText.includes("tag");

  if (isEmailType(type)) {
    const msg = isPaymentRequestEmailType(type, typeStr)
      ? formatPaymentRequestEmailTitle(messageStr)
      : getMultilingualString(message ?? {}) ||
        getMultilingualString(entityName ?? {}) ||
        (typeStr === "EMAIL_DAILY_DIGEST_SENT" ||
        type === EnumActivityLogType.EmailDailyDigestSent
          ? "Whereabouts emailed the Daily Digest"
          : typeStr === "EMAIL_BROADCAST_NOTIFICATION_SENT" ||
              type === EnumActivityLogType.EmailBroadcastNotificationSent
            ? "sent broadcast notification email(s)"
            : "Invite sent");
    return {
      title: msg,
      boldedText: "",
      cta: t("VIEW"),
      visual: getVisual(type),
      titleFormat: "default",
      hideAction,
    };
  }

  switch (type) {
    case EnumActivityLogType.EventSubmitted: {
      const boldedText = entityNameStr;
      const before = messageStr.split(boldedText)[0];
      const after = messageStr.split(boldedText)[1];
      return {
        title: before + "," + after,
        boldedText,
        cta: t("EVENT_SUBMITTED"),
        visual: getVisual(type),
        titleFormat: "eventSubmitted",
        hideAction,
      };
    }
    case EnumActivityLogType.OperatorCreated:
      return {
        title: t("OPERATOR_CREATED_LOG", { name: displayName }),
        boldedText: entityNameStr,
        cta: t("OPERATOR_CREATED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    case EnumActivityLogType.OperatorUpdated: {
      const cleanedMsg = messageStr.toLowerCase().replaceAll(".", "");
      const isAddMessage =
        cleanedMsg.includes("added") &&
        (cleanedMsg.includes("to directory") ||
          cleanedMsg.includes("to organization"));

      if (isAddMessage) {
        const match = cleanedMsg.match(
          /added (.+?) to (?:directory|organization)/,
        );
        const operatorName = match ? match[1] : entityNameStr;
        return {
          title: `${displayName} added to Directory`,
          boldedText: toTitleCase(operatorName),
          cta: t("OPERATOR_UPDATED"),
          visual: getVisual(type),
          titleFormat: "operatorAdded",
          hideAction,
        };
      }
      if (cleanedMsg.includes("approved changes to")) {
        return {
          title: `${displayName} approved changes to`,
          boldedText: entityNameStr,
          cta: t("OPERATOR_UPDATED"),
          visual: getVisual(type),
          titleFormat: "default",
          hideAction,
        };
      }
      return {
        title: `${displayName} ${cleanedMsg} ${t("FOR")}`,
        boldedText: entityNameStr,
        cta: t("OPERATOR_UPDATED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    }
    case EnumActivityLogType.DirectoryUpdated: {
      const cleanedMsg = messageStr.toLocaleLowerCase().replaceAll(".", "");
      return {
        title: `${displayName} ${cleanedMsg}`,
        boldedText: entityNameStr,
        cta: t("DIRECTORY_UPDATED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    }
    case EnumActivityLogType.OperatorStaffAdded: {
      const formatted =
        messageStr.slice(0, 1).toLocaleLowerCase() + messageStr.slice(1);
      return {
        title: `${displayName} ${formatted} ${t("TO")}`,
        boldedText: entityNameStr,
        cta: t("OPERATOR_STAFF_ADDED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    }
    case EnumActivityLogType.InquiryReceived: {
      const [title, boldedText] = messageStr.split(" via ");
      return {
        title: title + " via",
        boldedText: boldedText ?? "",
        cta: t("INQUIRY_RECEIVED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    }
    case EnumActivityLogType.BroadcastPublished:
      return {
        title: splitOnEntityName(messageStr, entityNameStr),
        boldedText: entityNameStr,
        cta: t("BROADCAST"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    case EnumActivityLogType.CourseStarted:
      return {
        title: splitOnEntityName(messageStr, entityNameStr),
        boldedText: entityNameStr,
        cta: t("COURSE_STARTED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    case EnumActivityLogType.CourseCompleted:
      return {
        title: splitOnEntityName(messageStr, entityNameStr),
        boldedText: entityNameStr,
        cta: t("COURSE_COMPLETED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    case EnumActivityLogType.BroadcastViewed:
      return {
        title: splitOnEntityName(messageStr, entityNameStr),
        boldedText: entityNameStr,
        cta: t("BROADCAST_VIEWED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    case EnumActivityLogType.ResourceViewed:
      return {
        title: splitOnEntityName(messageStr, entityNameStr),
        boldedText: entityNameStr,
        cta: t("RESOURCE_VIEWED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    case EnumActivityLogType.FileDownloaded:
      return {
        title: splitOnEntityName(messageStr, entityNameStr),
        boldedText: entityNameStr,
        cta: t("FILE_DOWNLOADED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    case EnumActivityLogType.PhotosDownloaded:
      return {
        title: splitOnEntityName(messageStr, entityNameStr),
        boldedText: entityNameStr,
        cta: t("PHOTOS_DOWNLOADED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    case EnumActivityLogType.VideoPlayed:
      return {
        title: splitOnEntityName(messageStr, entityNameStr),
        boldedText: entityNameStr,
        cta: t("VIDEO_PLAYED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    case EnumActivityLogType.FormCompleted:
    case EnumActivityLogType.FormDraftSaved:
    case EnumActivityLogType.FormResubmitted:
    case EnumActivityLogType.FormSubmissionApproved:
    case EnumActivityLogType.FormSubmissionRejected: {
      const boldedText = entityNameStr;
      const title =
        entityNameStr && messageStr
          ? (messageStr.split(entityNameStr)[0]?.trim() ?? messageStr)
          : messageStr || `${displayName} completed a form`;
      const ctaMap: Record<string, string> = {
        [EnumActivityLogType.FormCompleted]: t("FORM_COMPLETED"),
        [EnumActivityLogType.FormDraftSaved]: t("FORM_COMPLETED"),
        [EnumActivityLogType.FormResubmitted]: t("FORM_COMPLETED"),
        [EnumActivityLogType.FormSubmissionApproved]: t("FORM_COMPLETED"),
        [EnumActivityLogType.FormSubmissionRejected]: t("FORM_COMPLETED"),
      };
      return {
        title: title ?? "",
        boldedText,
        cta: ctaMap[type] ?? t("FORM_COMPLETED"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    }
    case EnumActivityLogType.OrderPurchased:
    case EnumActivityLogType.OrderPaymentPending:
    case EnumActivityLogType.OrderBookingRequest:
      return {
        title: messageStr,
        boldedText: "",
        cta: "",
        visual: getVisual(type),
        titleFormat: "default",
        hideAction: true,
      };
    case EnumActivityLogType.UserLoggedIn:
      return {
        title: entityNameStr,
        boldedText: t("LOGGED_IN"),
        cta: t("USER_LOGGED_IN"),
        visual: getVisual(type),
        titleFormat: "default",
        hideAction,
      };
    default:
      return {
        title: "",
        boldedText: "",
        cta: "",
        visual: "primary",
        titleFormat: "default",
        hideAction: true,
      };
  }
}
